import moment from 'moment';
import { boolean, mixed, object, string } from 'yup';

import { toMonthStart } from '../utils';
import { API_DATE_FORMAT } from '../../../constants';

import type { Moment } from 'moment';
import type {
  ITherapy,
  TherapyFieldName,
  TherapyFormSchemaOptions,
  TherapyFormValues,
  TherapyPayload,
  TherapyPayloadOptions,
  TherapyRowDescriptor,
} from './types';

export function toTherapyPayload(
  values: TherapyFormValues,
  options: TherapyPayloadOptions = {},
): TherapyPayload {
  const payload: TherapyPayload = {
    provider_name: values.provider_name,
    service_type: values.service_type,
    phone_number: values.phone_number || null,
    email: values.email,
    start_date: toMonthStart(values.start_date)?.format(API_DATE_FORMAT) ?? null,
    end_date: toMonthStart(values.end_date)?.format(API_DATE_FORMAT) ?? null,
    hours: values.hours === '' ? null : Number(values.hours),
    frequency: values.frequency,
    progress: values.progress,
    do_not_share_info_with_provider: values.do_not_share_info_with_provider,
  };
  if (options.includeRecordShared) {
    payload.is_record_shared = values.do_not_share_info_with_provider
      ? null
      : values.is_record_shared || null;
  }
  return payload;
}

export function toTherapyFieldPayload<K extends TherapyFieldName>(
  name: K,
  values: TherapyFormValues,
  options: TherapyPayloadOptions = {},
): TherapyPayload {
  // Send only the changed field so an older in-flight save cannot clobber a newer change.
  const data: TherapyPayload = {};
  const payload = toTherapyPayload(values, options);
  if (Object.hasOwn(payload, name)) {
    data[name] = payload[name];
  }
  // Checking the box clears share status: persist null and blank the auto-save snapshot.
  if (
    options.includeRecordShared &&
    name === 'do_not_share_info_with_provider' &&
    values.do_not_share_info_with_provider
  ) {
    data.is_record_shared = null;
    values.is_record_shared = '';
  }
  return data;
}

export function mapTherapyRowToValues(row: ITherapy): TherapyFormValues {
  return {
    provider_name: row.provider_name ?? '',
    service_type: row.service_type ?? '',
    phone_number: row.phone_number ?? '',
    email: row.email ?? '',
    start_date: toMonthStart(row.start_date ? moment(row.start_date) : null),
    end_date: toMonthStart(row.end_date ? moment(row.end_date) : null),
    hours: row.hours != null ? String(row.hours) : '',
    frequency: row.frequency ?? '',
    progress: row.progress ?? '',
    do_not_share_info_with_provider: row.do_not_share_info_with_provider ?? false,
    is_record_shared: row.is_record_shared ?? '',
  };
}

export function getTherapyRowLabel(row: ITherapy): string {
  return `${row.provider_name ?? ''} - ${row.service_type ?? ''}`;
}

export function getTherapyDeleteMessage(row: TherapyRowDescriptor): string {
  if (!row.provider_name && !row.service_type) {
    return 'Are you sure you want to delete this therapy?';
  }
  const therapyDescriptor = [row.provider_name, row.service_type].filter(Boolean).join(' - ');
  return `Are you sure you want to delete therapy "${therapyDescriptor}"?`;
}

export function buildTherapyFormSchema(options: TherapyFormSchemaOptions = {}) {
  let providerNameSchema = string().trim().required();
  if (options.providerNameMinLength) {
    providerNameSchema = providerNameSchema.min(
      options.providerNameMinLength,
      `Provider name must be at least ${options.providerNameMinLength} characters`,
    );
  }
  return object({
    provider_name: providerNameSchema,
    service_type: string().required(),
    phone_number: string()
      .trim()
      .matches(/^\d{10}$/, 'Enter a 10-digit phone number')
      .required('Phone number is required'),
    email: string().trim().email('Enter a valid email').required(),
    start_date: mixed<Moment>().nullable().required(),
    end_date: mixed<Moment>()
      .nullable()
      .defined()
      .test(
        'after-or-equal-start',
        'End month cannot be before start month',
        function endAfterStart(value) {
          const start = this.parent.start_date;
          if (!moment.isMoment(value) || !moment.isMoment(start)) return true;
          return !value.isBefore(start, 'month');
        },
      ),
    hours: string().trim().matches(/^\d+$/, 'Enter a whole number').required(),
    frequency: string().required(),
    progress: string().required(),
    do_not_share_info_with_provider: boolean().defined(),
    is_record_shared: options.includeRecordShared
      ? string()
          .defined()
          .when('do_not_share_info_with_provider', {
            is: true,
            then: (schema) => schema,
            otherwise: (schema) => schema.required(),
          })
      : string().defined(),
  });
}
